import { SelectionAiActions } from '@/components/tiptap/shared/selection-ai-actions';
import {
  BoldButton,
  BulletListButton,
  ItalicButton,
  OrderedListButton,
  UnderlineButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { useEmphasisDisabledTooltip } from '@/features/copySettings';
import { selectSectionFormattingStates } from '@heidi/editor/core';
import { type ChainedCommands, type Editor, useEditorState } from '@tiptap/react';
import { useCallback } from 'react';

const ICON_SIZE = 16;

/** The section-body range every tool operates on. */
type GetTargetRange = () => { from: number; to: number } | null;

/**
 * Formatting icon row (bold / italic / underline / lists) embedded in the
 * section kebab dropdown (selection → Ask Heidi v5), targeting the whole
 * section body. Mirrors the text-selection bubble menu's first icon row.
 *
 * The section body is NEVER left selected: a live selection would also summon
 * the text-selection bubble menu next to this dropdown. Formatting applies
 * select → toggle → collapse inside one dispatched chain (intermediate
 * selection states never render).
 */
export const SectionFormattingRow = ({
  editor,
  getTargetRange,
  emphasisDisabled,
}: {
  editor: Editor;
  getTargetRange: GetTargetRange;
  /**
   * Disable the bold/italic/underline buttons — the "Bold & italics" copy
   * setting is off and those marks are wiped from the note display.
   * List buttons are unaffected.
   */
  emphasisDisabled?: boolean;
}) => {
  const { nodes } = editor.schema;
  const emphasisDisabledTooltip = useEmphasisDisabledTooltip();
  const { isBold, isItalic, isUnderline, isBulletList, isOrderedList } = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) =>
      selectSectionFormattingStates(currentEditor, getTargetRange()),
  });

  const applyToTarget = useCallback(
    (toggle: (chain: ChainedCommands) => ChainedCommands) => {
      const range = getTargetRange();
      if (!range) return;
      toggle(editor.chain().setTextSelection(range)).setTextSelection(range.to).run();
    },
    [editor, getTargetRange]
  );

  return (
    <div
      data-testid="section-formatting-row"
      // Match the AI actions list below (and the bubble menu's toolbar) at p-2
      // so the row's left/right padding lines up with the rest of the menu.
      className="flex flex-row items-center gap-0.5 p-2"
    >
      <BoldButton
        onClick={() => applyToTarget((chain) => chain.toggleBold())}
        active={isBold}
        disabled={emphasisDisabled}
        disabledTooltip={emphasisDisabledTooltip}
        size="md"
        iconSize={ICON_SIZE}
      />
      <ItalicButton
        onClick={() => applyToTarget((chain) => chain.toggleItalic())}
        active={isItalic}
        disabled={emphasisDisabled}
        size="md"
        disabledTooltip={emphasisDisabledTooltip}
        iconSize={ICON_SIZE}
      />
      <UnderlineButton
        onClick={() => applyToTarget((chain) => chain.toggleUnderline())}
        active={isUnderline}
        disabled={emphasisDisabled}
        size="md"
        iconSize={ICON_SIZE}
        disabledTooltip={emphasisDisabledTooltip}
      />
      {nodes.bulletList && (
        <BulletListButton
          onClick={() => applyToTarget((chain) => chain.toggleBulletList())}
          active={isBulletList}
          size="md"
          iconSize={ICON_SIZE}
        />
      )}
      {nodes.orderedList && (
        <OrderedListButton
          onClick={() => applyToTarget((chain) => chain.toggleOrderedList())}
          active={isOrderedList}
          size="md"
          iconSize={ICON_SIZE}
        />
      )}
    </div>
  );
};

/**
 * AI actions list (Rewrite / View transcript / Check evidence / Edit with AI)
 * embedded in the section kebab dropdown, targeting the whole section body.
 * Thin wrapper over `SelectionAiActions` so the section kebab imports its
 * selection tools from a single module (keeps the ProseMirror-plugin import
 * chain behind one mock in tests).
 */
export const SectionAiActionsList = ({
  editor,
  getTargetRange,
  onAiActionStart,
}: {
  editor: Editor;
  getTargetRange: GetTargetRange;
  /** Fired when an AI action starts so the host dropdown can close. */
  onAiActionStart?: () => void;
}) => (
  <SelectionAiActions
    editor={editor}
    onActionStart={onAiActionStart}
    getTargetRange={getTargetRange}
    // The section kebab draws its own DropdownMenuSeparator above this list,
    // so suppress the AI actions' own top divider to avoid a double line.
    showTopBorder={false}
  />
);
